import React, { useEffect, useState } from 'react';
import '../styles/orders.css';
import GoBackButton from './GoBackButton';
import { useTheme } from '../hooks/useTheme';

export default function OrdersHistory() {
  const [orders, setOrders] = useState([]);
  const [phone, setPhone] = useState('');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  useEffect(() => {
    const storedPhone = localStorage.getItem('userPhone');
    if (storedPhone) {
      setPhone(storedPhone);
    }
  }, []);

  useEffect(() => {
    if (!phone) {
      console.warn('Phone number not available yet');
      return;
    }

    fetch(`/api/orders/${phone}/orders`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch orders');
        return res.json();
      })
      .then(data => {
        setOrders(data || []);
        setFilteredOrders(data || []);
      })
      .catch((err) => console.error('Failed to load orders:', err));
  }, [phone]);

  useEffect(() => {
    // Apply date filters
    let filtered = orders;

    if (startDate || endDate) {
      filtered = orders.filter(order => {
        const orderDate = new Date(order.createdDate);
        const start = startDate ? new Date(startDate) : new Date('2000-01-01');
        const end = endDate ? new Date(endDate) : new Date('2099-12-31');

        return orderDate >= start && orderDate <= end;
      });
    }

    setFilteredOrders(filtered);

    // Drop any selection that's no longer visible under the current filter,
    // otherwise "select all" can silently include hidden rows.
    setSelectedOrders(prev => {
      const stillVisible = new Set(filtered.map(o => o.id));
      return new Set([...prev].filter(id => stillVisible.has(id)));
    });
  }, [startDate, endDate, orders]);

  useEffect(() => {
    setSelectAll(filteredOrders.length > 0 && selectedOrders.size === filteredOrders.length);
  }, [selectedOrders, filteredOrders]);

  console.log('orders:', orders);

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const handlePrint = async () => {
    if (selectedOrders.size === 0) {
      alert('الرجاء تحديد طلب واحد على الأقل / Please select at least one order');
      return;
    }

    setIsPrinting(true);
    try {
      const requestBody = {
        phone: phone,
        selectedOrderIds: Array.from(selectedOrders),
        startDate: null,
        endDate: null
      };

      const response = await fetch('/api/orders/print', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let message = 'Failed to generate PDF';
        try {
          const errJson = await response.json();
          if (errJson?.error) message = errJson.error;
        } catch (_) {
          // response wasn't JSON, fall back to the generic message
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders-${new Date().getTime()}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Print error:', error);
      alert('خطأ في طباعة الطلبات / Error printing orders: ' + error.message);
    } finally {
      setIsPrinting(false);
    }
  };

  const statusLabel = (status) => status || '—';

  return (
    <div className={`orders-history-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} dir="rtl">
      <div className="orders-header">
        <GoBackButton />
        <h1 className="orders-title">سجل الطلبات</h1>
      </div>

      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p className="empty-order-message">لا يوجد سجل طلبات</p>
        </div>
      ) : (
        <>
          {/* Filters and Actions Section */}
          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="startDate">من تاريخ</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">إلى تاريخ</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
            </div>

            <div className="filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="checkbox-input"
                />
                <span>تحديد الكل </span>
              </label>
            </div>

            <button
              className={`print-button ${selectedOrders.size === 0 ? 'disabled' : ''}`}
              onClick={handlePrint}
              disabled={isPrinting || selectedOrders.size === 0}
            >
              {isPrinting ? 'جاري الطباعة...' : `🖨️ طباعة  (${selectedOrders.size})`}
            </button>
          </div>

          {/* Desktop table view */}
          <div className="orders-table-wrapper desktop-view">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>طلب</th>
                  <th>التاريخ</th>
                  <th>الحالة</th>
                  <th>المبلغ الإجمالي</th>
                  <th>المواد</th>
                  <th className="col-check">طباعة</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className={selectedOrders.has(order.id) ? 'row-selected' : ''}>
                    <td className="order-number-cell">{order.orderNumber}</td>
                    <td>{new Date(order.createdDate).toLocaleDateString('ar-SA')}</td>
                    <td>
                      <span className={`status-badge status-${(order.status || '').toLowerCase()}`}>
                        {statusLabel(order.status)}
                      </span>
                    </td>
                    <td className="value-highlight">{order.totalPrice?.toLocaleString()} د.ع</td>
                    <td className="items-cell">
                      <table className="items-subtable">
                        <tbody>
                          {order.items?.map((item, index) => (
                            <tr key={index}>
                              <td className="items-subtable-name">
                                <div className="item-name">{item.productName}</div>
                                {item.combinationText && (
                                  <div className="item-variant">{item.combinationText}</div>
                                )}
                              </td>
                              <td className="items-subtable-qty">
                                <span className="item-qty-badge">{item.qty}</span>
                              </td>
                              <td className="items-subtable-image">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.productName}
                                    className="item-thumb"
                                  />
                                ) : (
                                  <div className="item-thumb item-thumb-placeholder" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                    <td className="col-check">
                      <input
                        type="checkbox"
                        checked={selectedOrders.has(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                        className="card-checkbox"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="orders-grid mobile-view">
            {filteredOrders.map((order) => (
              <div key={order.id} className="order-card-modern">
                <div className="order-card-header">
                  <div className="selection-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={() => handleSelectOrder(order.id)}
                      className="card-checkbox"
                    />
                  </div>
                  <div className="order-number">طلب{order.orderNumber}</div>
                </div>

                <div className="order-card-body">
                  <div className="order-info-row">
                    <span className="label">التاريخ:</span>
                    <span className="value">{new Date(order.createdDate).toLocaleDateString('ar-SA')}</span>
                  </div>

                  <div className="order-info-row">
                    <span className="label">الحالة:</span>
                    <span className={`status-badge status-${(order.status || '').toLowerCase()}`}>
                      {statusLabel(order.status)}
                    </span>
                  </div>

                  <div className="order-info-row">
                    <span className="label">المبلغ الإجمالي:</span>
                    <span className="value-highlight">{order.totalPrice?.toLocaleString()} د.ع</span>
                  </div>

                  <div className="divider"></div>

                  <div className="items-header">المواد ({order.items?.length || 0})</div>

                  <div className="order-items-grid">
                    {order.items?.map((item, index) => (
                      <div key={index} className="item-mini">
                        <div className="item-qty-badge">{item.qty}</div>
                        <div className="item-description">
                          <div className="item-name">{item.productName}</div>
                          {item.combinationText && (
                            <div className="item-variant">{item.combinationText}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredOrders.length === 0 && orders.length > 0 && (
            <div className="no-results">
              <p>لا توجد طلبات مطابقة لمعايير البحث / No orders matching the search criteria</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}